import { AlertCircle, Check, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import {
  INPUT_BINDING_COPY,
  fillerName,
  filledByNoRow,
  fillsSubject,
  groupTargetsByColumn,
  targetKey,
  type BindingSubject,
  type BindingTarget,
  type BoundDocumentId,
} from "../../lib/analyticsFeeds";

/**
 * The binding editor: which of the workflows' file parameters this row's subject
 * fills.
 *
 * The assignment target is the **pair** (column, `param_key`). A per-document
 * boolean carries neither coordinate, so it cannot express a binding once a test
 * table holds two analytics columns, or once one column takes two file inputs —
 * which is why every row here is keyed on the pair and never on the subject.
 *
 * The subject is the subject of the list rather than one of its rows, and it is
 * the only thing a tick here can name. Every item is a workflow input, so the
 * question is *which input does this row's subject fill* — answerable on any row
 * without naming a file that row is not for. A control that could name a foreign
 * file is not withheld here; there is nowhere in this list to express one.
 *
 * The column half of the pair is read from a heading over its parameters rather
 * than from a prefix on each of them, and only once more than one column
 * contributes: repeating one column's name down a list it wholly owns is the
 * same word N times, and it crowds out the half of the pair the auditor is
 * actually choosing between.
 *
 * That heading names the **workflow** the parameters were projected from, not
 * the column. The parameters belong to the workflow's activated version — the
 * column declares none of them — so the workflow's name is the one an auditor
 * can match against what they built in Optro Analytics.
 *
 * A subject fills as many parameters as it is bound to, so the rows are
 * independent two-state answers and not one choice out of the list. They wear no
 * toggle chrome: a checkmark on the answered row is how the rest of the product
 * marks a selection, and a column of empty boxes reads as a form waiting to be
 * filled in rather than as a set of parameters most of which are already
 * answered somewhere.
 *
 * Every parameter stays offered whoever wrote the binding. A prefill is a guess
 * at what answers a parameter, and one drawn as a fact is a guess the auditor
 * cannot correct — there would then be no route between the two classes in
 * either direction, which is the state the agent's first guess would freeze the
 * test in.
 *
 * A parameter holds exactly one answer, so an item that names another filler is
 * still offered: each write replaces whatever the parameter held, and making the
 * auditor clear the old answer first would take a parameter through an
 * unanswered state to change its mind.
 *
 * Replacing an answer is not emptying it, though, and an answer no row is the
 * subject of cannot be emptied by a tick on any row of this list. Those items
 * carry a clear of their own, beside the note that names their filler. It is a
 * second hit target rather than a second meaning for the tick: a tick that
 * cleared where it happened to be pointed at a foreign answer would be one
 * control saying two opposite things, told apart only by state the auditor
 * cannot see.
 *
 * The whole display is a function of `targets`, which the caller reads from the
 * bindings endpoint. Nothing is held here at all, so an assignment survives a
 * reload because it was never in a component to begin with.
 */
interface InputBindingPickerProps {
  targets: readonly BindingTarget[];
  /** What a tick on one of these parameters says fills it. */
  subject: BindingSubject;
  /**
   * The session's file names, so an item can say which file fills it.
   *
   * Taken from the page rather than read here: a binding stores a document id,
   * and the names of the table's documents are the page's own state.
   */
  documentNames: ReadonlyMap<BoundDocumentId, string>;
  onFill: (target: BindingTarget) => void;
  onClear: (target: BindingTarget) => void;
  pending?: boolean;
}

/**
 * One parameter this row's subject can fill.
 *
 * The toggle is the whole width of the item, so a press lands anywhere along it
 * and the item's own text is its accessible name — there is no separate label
 * element, and so no id to pair one to.
 *
 * The builder's description of the parameter, where they wrote one, sits under
 * the name and above the note: it says what belongs in the slot, which is how
 * the auditor tells two similarly named inputs apart before asking what fills
 * either.
 *
 * The note at the foot of the item says what fills the parameter today, or
 * warns that a required one is unfilled. It is the answer to *is this input
 * fed, and by what*, which is the question the auditor opened the list with,
 * and it renders against the parameter it is true of rather than at the foot of
 * the list.
 *
 * `onClear` is null on every parameter some row of the table answers, because
 * that row's own toggle is what empties it. Where it is given, it draws a
 * control beside the toggle rather than inside it: the two are siblings because
 * nested buttons are not expressible in HTML, and because the toggle keeps the
 * note in its accessible name either way.
 */
function ParameterRow({
  target,
  selected,
  note,
  unfilledRequired,
  disabled,
  onToggle,
  onClear,
}: {
  target: BindingTarget;
  selected: boolean;
  note: string | null;
  unfilledRequired: boolean;
  disabled: boolean;
  onToggle: () => void;
  onClear: (() => void) | null;
}) {
  return (
    <div className="flex items-end">
      <button
        type="button"
        // Checkbox semantics behind the checkmark: a subject fills as many
        // parameters as it is bound to, so these are independent toggles and not
        // one choice out of the list.
        role="checkbox"
        aria-checked={selected}
        disabled={disabled}
        onClick={onToggle}
        className="flex min-w-0 flex-1 items-start gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-stone-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-stone-950 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {/* Held in the layout while unselected rather than dropped, so a name does
          not shift sideways as the auditor works down the list. */}
        <Check
          aria-hidden="true"
          className={cn(
            "mt-0.5 h-4 w-4 shrink-0",
            selected ? "opacity-100" : "opacity-0",
          )}
        />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span>
            {/* The visible line is the parameter name alone; the row's accessible
              name carries the pair. The target *is* the pair, and without this
              two columns declaring the same parameter label are one word, twice,
              to a screen reader. The column names the pair even though the
              heading above names the workflow: the column is the coordinate the
              write carries, and two columns can be bound to workflows sharing a
              name. */}
            <span className="sr-only">{`${target.columnName} · ${target.label}`}</span>
            <span aria-hidden="true">{target.label}</span>
            {/* The glyph repeats what the note below says in words, for a reader
              scanning the names alone. Hidden from assistive technology for the
              same reason — the note carries it. */}
            {unfilledRequired && (
              <AlertCircle
                aria-hidden="true"
                className="ml-1 inline h-3 w-3 shrink-0 text-red-600"
              />
            )}
            {target.required && (
              <span className="ml-1 text-xs text-font-secondary">
                ({INPUT_BINDING_COPY.required})
              </span>
            )}
          </span>
          {/* Inside the toggle so the description joins its accessible name,
            heard after the column-qualified name it explains. */}
          {target.description !== null && (
            <span className="break-words text-xs text-font-secondary">
              {target.description}
            </span>
          )}
          {note !== null && (
            <span
              className={cn(
                "text-xs italic",
                unfilledRequired ? "text-red-600" : "text-font-secondary",
              )}
            >
              {`— ${note}`}
            </span>
          )}
        </span>
      </button>
      {onClear !== null && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled}
          onClick={onClear}
          // The pair, because the copy alone is the same sentence on every
          // parameter it appears beside, and a reader moving between buttons
          // hears only their names.
          aria-label={`${INPUT_BINDING_COPY.clearFiller} — ${target.columnName} · ${target.label}`}
          className="h-7 w-7 shrink-0 rounded text-font-secondary"
        >
          <X aria-hidden="true" className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

export function InputBindingPicker({
  targets,
  subject,
  documentNames,
  onFill,
  onClear,
  pending = false,
}: InputBindingPickerProps) {
  if (targets.length === 0) return null;

  const groups = groupTargetsByColumn(targets);
  // A heading earns its place only by telling two groups apart. Over a list one
  // workflow wholly owns it is the same word N times, and it crowds out the
  // parameter names the auditor is choosing between.
  const showsHeadings =
    new Set(targets.map((target) => target.columnId)).size > 1;

  return (
    <div className="flex flex-col gap-2">
      <div className="px-2 text-xs font-medium text-font-secondary">
        {INPUT_BINDING_COPY.pickerHeading}
      </div>
      {/* Capped and scrolled rather than left to grow: the list sits in a
          popover, and a workflow declaring a dozen file inputs would otherwise
          run past the bottom of the viewport. */}
      <ul className="flex max-h-72 flex-col gap-1 overflow-y-auto">
        {groups.map((group) => (
          <li key={group.columnId}>
            {showsHeadings && (
              <div className="px-2 py-1 text-xs font-medium uppercase text-font-secondary">
                {group.heading}
              </div>
            )}
            <ul className="flex flex-col">
              {group.targets.map((target) => {
                const selected = fillsSubject(target, subject);
                // The subject's own answer is said by the checkmark, so naming
                // it again below would say it twice, once inertly.
                const filler = selected
                  ? null
                  : fillerName(target, documentNames);
                const unfilledRequired =
                  target.required && target.boundSource === null;
                // The row this parameter's answer belongs to empties it, so the
                // clear is drawn only where there is no such row. `selected`
                // rules out the row in hand, which the table can fail to name
                // and so cannot always be recognised from the answer alone.
                const clearableHere =
                  !selected && filledByNoRow(target, documentNames);
                return (
                  <li key={targetKey(target)} className="pb-1">
                    <ParameterRow
                      target={target}
                      selected={selected}
                      note={
                        unfilledRequired ? INPUT_BINDING_COPY.unfilled : filler
                      }
                      unfilledRequired={unfilledRequired}
                      disabled={pending}
                      // The tick says one thing on every row of the list —
                      // *this row fills it* — and its second press on the row
                      // that already does is what empties the parameter.
                      onToggle={() =>
                        selected ? onClear(target) : onFill(target)
                      }
                      onClear={clearableHere ? () => onClear(target) : null}
                    />
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}
